import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { normalizeSubmission } from "src/pages/Trackers/submissionStorage";
import { CW, Eyebrow, Panel, TextLink } from "./cwUi";

/**
 * "Latest check-in" panel (design PDF page 2, left column): number answers
 * as tiles with the change vs the previous check-in, text answers below.
 * Moved out of ClientReviewPage (CLIENTS, 3 Nov) so the review page and the
 * new client workspace (/coach/clients/:key) show the same thing.
 * submission = normalizeSubmission(row); history = raw rows (newest first).
 */

const CONTACT_FIELD = /^(first\s*name|last\s*name|full\s*name|name|e-?mail(\s*address)?|phone(\s*number)?|mobile(\s*number)?|password)$/i;
const plain = (v) =>
  String(Array.isArray(v) ? v.join(", ") : v ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
const asNumber = (v) => {
  const m = String(v ?? "").trim().match(/^-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : null;
};

const unitOf = (v) => {
  const m = String(plain(v)).match(/^-?\d+(?:\.\d+)?\s*([a-zA-Z%]{1,6})?/);
  return m && m[1] ? ` ${m[1]}` : "";
};

/** Number answer as a tile (WEIGHT 82.6 kg, −0.4 kg vs previous). */
function NumberTile({ label, value, previous }) {
  const now = asNumber(value);
  const before = asNumber(previous);
  const delta = now != null && before != null ? Math.round((now - before) * 100) / 100 : null;
  return (
    <Box sx={{ bgcolor: CW.soft, borderRadius: "10px", p: 1.5, minWidth: 0 }}>
      <Eyebrow sx={{ fontSize: 10.5 }}>{label}</Eyebrow>
      <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 700, color: CW.ink, mt: 0.5, lineHeight: 1.2 }} noWrap>
        {plain(value) || "—"}
      </Typography>
      <Typography sx={{ fontSize: 12, color: CW.muted, mt: 0.5 }}>
        {delta == null
          ? before == null
            ? "First check-in"
            : " "
          : delta === 0
            ? "Same as previous"
            : `${delta > 0 ? "+" : "−"}${Math.abs(delta)}${unitOf(value)} vs previous`}
      </Typography>
    </Box>
  );
}

/** Text answer: question in bold, answer below (PDF "How did training go?"). */
function TextAnswer({ label, value, previous }) {
  const text = plain(value);
  const prev = plain(previous);
  return (
    <Box sx={{ mt: 2.25 }}>
      <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: CW.ink }}>{label}</Typography>
      <Typography sx={{ fontSize: 14.5, color: CW.muted, mt: 0.5, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {text || "—"}
      </Typography>
      {prev && prev !== text && prev.length <= 60 ? (
        <Typography sx={{ fontSize: 12, color: CW.muted, mt: 0.25 }}>Last check-in: {prev}</Typography>
      ) : null}
    </Box>
  );
}

export default function LatestCheckInPanel({
  submission,
  history = [],
  onOpenFull,
  title = "Latest check-in",
  subtitle = "Compared with the previous submission",
  info = "review.latest",
  footer,
  sx,
}) {
  const previousAnswers = useMemo(() => {
    if (!submission) return {};
    const at = new Date(submission.submittedAt).getTime();
    const prev = (history || [])
      .map((row) => normalizeSubmission(row))
      .filter((row) => row && row.id !== submission.id && new Date(row.submittedAt).getTime() < at)
      .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))[0];
    return prev ? prev.answers || {} : {};
  }, [history, submission]);

  const answers = useMemo(
    () => Object.entries(submission?.answers || {}).filter(([key]) => !CONTACT_FIELD.test(String(key).trim())),
    [submission]
  );
  const numberAnswers = answers.filter(([, v]) => asNumber(plain(v)) != null && plain(v).length <= 20);
  const textAnswers = answers.filter(([, v]) => !(asNumber(plain(v)) != null && plain(v).length <= 20));

  return (
    <Panel title={title} info={info} sx={sx}>
      <Typography sx={{ fontSize: 13, color: CW.muted, mt: -1, mb: 2 }}>{subtitle}</Typography>
      {answers.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: CW.muted }}>No answers stored on this check-in.</Typography>
      ) : (
        <>
          {numberAnswers.length ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              {numberAnswers.map(([key, value]) => (
                <NumberTile key={key} label={key} value={value} previous={previousAnswers[key]} />
              ))}
            </Box>
          ) : null}
          {textAnswers.map(([key, value]) => (
            <TextAnswer key={key} label={key} value={value} previous={previousAnswers[key]} />
          ))}
        </>
      )}
      <Box sx={{ flex: 1 }} />
      {footer || null}
      {onOpenFull ? (
        <TextLink onClick={onOpenFull} sx={{ mt: 3 }} title="Old submission page (charts, history, status)">
          View full answers and history →
        </TextLink>
      ) : null}
    </Panel>
  );
}
